import { useState, useMemo, useCallback } from "react";
import { useUser } from "../../services/user-context";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../services/api";
import { NotificationButton } from "../../components/NotificationButton";
import { Summary } from "./components/Summary";
import { PractitionerSelector } from "./components/PractitionerSelector";
import { HistoryList } from "./components/HistoryList";
import dayjs from "dayjs";
import { BackButton } from "../../components/Button/BackButton";
import { useNavigate } from "react-router-dom";

export function CallHistory() {
  const [selectedPractitioner, setSelectedPractitioner] = useState<string>("all");
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState<string>((dayjs().month() + 1).toString());
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().year().toString());
  const [sort, setSort] = useState<string>("newest");

  const handleSortChange = useCallback((value: string) => setSort(value), []);

  const { data: sessionHistoryMetrics, isLoading: isLoadingSessionHistoryMetrics } = useQuery({
    queryKey: ["session-history-metrics", selectedMonth, selectedYear],
    queryFn: () => api.getSessionHistoryMetrics({ month: selectedMonth, year: selectedYear }),
    enabled: !!selectedMonth && !!selectedYear,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const filterForTable = useMemo(() => ({ practitionerType: selectedPractitioner, month: selectedMonth, year: selectedYear }), [selectedPractitioner, selectedMonth, selectedYear]);

  const handleRowClick = useCallback((row: any) => {
    const hasEndReason = !!row?.endReason || !!row?.end_reason;
    if (hasEndReason) return;
    navigate(`/praxisplay/session/${row.sessionID}`, { state: { source: "history" } });
  }, [navigate]);

  const handlePractitionerChange = useCallback((value: string) => {
    setSelectedPractitioner(value);
  }, []);

  const summaryData = sessionHistoryMetrics?.data;
  return (
    <div className="p-4 md:p-6 !pt-[80px] pb-8 lg:!pt-4 md:flex md:flex-col md:h-[100vh] lg:h-full">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <BackButton
            onClick={() => {
              navigate("/dashboard");
            }}
          />
          <span className="text-black text-sm">Dashboard</span>
        </div>
        <NotificationButton />
      </div>
      <div className="flex-col justify-start items-start tall2:gap-2 gap-2 md:gap-4 inline-flex">
        <div className="flex flex-col gap-1">
          <div data-testid="call-history-title" className="text-[#333] text-[34px] font-bold">PraxisPlay Call Log</div>
          <div className="justify-start text-text-gray-500 text-base font-medium">View all your previous PraxisPlay Sessions</div>
        </div>
      </div>
      <>
        <div className="md:self-center md:flex-1 tall2:h-[calc(100vh-118px)] h-auto overflow-hidden w-full tall2:mt-3 mt-4 md:mt-7 px-6 py-6 bg-white rounded-2xl shadow border flex-col justify-start items-start tall2:gap-2 gap-4 flex">
          <Summary isLoading={isLoadingSessionHistoryMetrics} summaryData={summaryData} selectedMonth={selectedMonth} selectedYear={selectedYear} setSelectedMonth={setSelectedMonth} setSelectedYear={setSelectedYear} />
          <PractitionerSelector onChange={handlePractitionerChange} value={selectedPractitioner} />
          <HistoryList
            filter={filterForTable}
            onRowClick={handleRowClick}
            enablePagination={true}
            sort={sort}
            onSortChange={handleSortChange}
          />
        </div>
      </>
    </div>
  );
}
